
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  model,
  signal,
  ViewChild,
  viewChildren
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { IconComponent } from './../../lib-icon/icon.component';
import { DropdownItem } from './dropdown.model';

@Component({
  selector: 'lib-dropdown',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, IconComponent],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent<T> {
  options = input<DropdownItem<T>[]>([]);
  placeholder = input<string>('');
  showFilter = input(false);
  filterPlaceholder = input<string>();
  inputFilterIcon = input<string | null>('MagnifyingGlass');
  selectedOption = model<DropdownItem<T>>();
  isDropdownOpen = signal(false);
  filterCtrl = signal<FormControl<string | null>>(new FormControl('', { updateOn: 'change' }));
  filterValue = toSignal(this.filterCtrl().valueChanges.pipe(map(value => value || '')));

  @ViewChild('dropdown', { static: false }) dropdownElement?: ElementRef;
  readonly optionElements = viewChildren<ElementRef>('optionElements');

  filteredOptions = computed<DropdownItem<T>[]>(() =>
    this.options().filter(
      (option: DropdownItem<T>) =>
        !this.filterValue() || !this.showFilter() ||
        option.description.toLocaleLowerCase().includes(this.filterValue()?.toLocaleLowerCase() as string),
    ),
  );

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  closeDropdown(e: Event) {
    e.stopPropagation();
    this.isDropdownOpen.update(() => false);
  }

  selectOption(selectedOption: DropdownItem<T>) {
    this.selectedOption.set(selectedOption);
    this.isDropdownOpen.set(false);
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!this.isDropdownOpen() || !this.optionElements()) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusNextOption();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusPreviousOption();
    }
  }

  private focusNextOption() {
    const optionElements = this.optionElements();
    if (!optionElements) {
      return;
    }
    const focusedIndex = this.getFocusedOptionElementIndex() > -1 ? this.getFocusedOptionElementIndex() : -1;
    const nextIndex = (focusedIndex + 1) % optionElements?.length;
    this.focusOption(nextIndex);
  }

  private focusPreviousOption() {
    const optionElements = this.optionElements();
    if (!optionElements) {
      return;
    }
    const focusedIndex =
      this.getFocusedOptionElementIndex() > -1 ? this.getFocusedOptionElementIndex() : optionElements.length;
    const previousIndex = (focusedIndex - 1 + optionElements.length) % optionElements.length;
    this.focusOption(previousIndex);
  }

  private getFocusedOptionElementIndex(): number {
    const optionElements = this.optionElements();
    if (!optionElements) return -1;

    return optionElements.findIndex(element => element.nativeElement === document.activeElement);
  }

  private focusOption(index: number) {
    const option = this.optionElements()?.at(index);
    option?.nativeElement.focus();
    option?.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  @HostListener('focusout', ['$event'])
  onBlur(event: FocusEvent) {
    const target = event.relatedTarget as HTMLElement;

    if (!this.dropdownElement?.nativeElement.contains(target)) {
      this.isDropdownOpen.set(false);
    }
  }
}
